// Persona constants for the two onboarding workflows. `individual` = the
// workspace owner is the patient, connecting their own records. `organization` =
// the owner asks other people to connect records on their behalf (a family
// head, a patient-advocate lead, a legal firm). Shared by the server
// (organization.account_type) and the frontend forms so the literals live once.

export const ACCOUNT_TYPE = {
  INDIVIDUAL: "individual",
  ORGANIZATION: "organization",
} as const;

/** Which onboarding workflow a workspace belongs to; null until the owner answers. */
export type AccountType = (typeof ACCOUNT_TYPE)[keyof typeof ACCOUNT_TYPE];

/** Every account type, in form order. */
export const ACCOUNT_TYPES = [ACCOUNT_TYPE.INDIVIDUAL, ACCOUNT_TYPE.ORGANIZATION] as const;

// Sub-persona for the "records for other people" branch: is the party asking
// for records one person or a registered organization? `person` deliberately
// avoids reusing "individual", which on `account_type` means the opposite thing
// (a workspace holding the owner's own records).

export const REQUESTER_TYPE = {
  PERSON: "person",
  ORGANIZATION: "organization",
} as const;

/** Who is asking for other people's records; null on the own-records branch. */
export type RequesterType = (typeof REQUESTER_TYPE)[keyof typeof REQUESTER_TYPE];

/** Every requester type, in form order. */
export const REQUESTER_TYPES = [REQUESTER_TYPE.PERSON, REQUESTER_TYPE.ORGANIZATION] as const;

/** Roles a member can hold inside an organization workspace, for the signup dropdown. */
export const DESIGNATIONS = [
  "Physician",
  "Nurse",
  "Care coordinator",
  "Case manager",
  "Administrator",
  "Billing / Operations",
  "IT / Engineering",
  "Caregiver / Family member",
  "Other",
] as const;

/** A designation the member picked when their organization workspace was created. */
export type Designation = (typeof DESIGNATIONS)[number];
